import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Warning, ArrowSquareOut } from "@phosphor-icons/react";
import { useAuth } from "../auth/AuthContext";

/**
 * v0.4.37 Q3 (operator sign-off 2026-06-04, defaults a/a/a/a/yes).
 *
 * First-run amber banner that nudges the admin to rename the
 * seeded `Test Co` default client BEFORE creating real filings.
 *
 * Trigger condition (Q3A=a):
 *   - the ONLY client in the database is named exactly "Test Co"
 *     AND that client carries `is_default == True`.
 *   - i.e. `clients.length === 1 && c.name === "Test Co"
 *           && c.is_default === true`.
 *
 * Placement (Q3B=a):
 *   - Top of Dashboard, above the Stepper. Full-width amber strip.
 *
 * CTA (Q3C=a):
 *   - ADMIN sees a clickable link to `/admin/clients`.
 *   - OPERATOR sees a static line: "Ask your administrator to
 *     rename the default client."
 *
 * Dismissal (Q3D=a):
 *   - Auto-dismisses the moment the client name is no longer
 *     "Test Co". No manual × button; the banner is the contract.
 */
export const FirstRunRenameBanner = ({ clients }) => {
  const { t } = useTranslation();
  const { admin } = useAuth();

  if (!Array.isArray(clients) || clients.length !== 1) return null;
  const only = clients[0];
  if (!only || only.name !== "Test Co" || only.is_default !== true) {
    return null;
  }

  const isAdmin = admin?.role === "ADMIN";

  return (
    <div
      data-testid="first-run-rename-banner"
      className="mx-6 mt-4 flex items-center gap-3 rounded-xl border border-amber-300 dark:border-amber-400/40 bg-amber-50 dark:bg-amber-900/40 px-4 py-3 text-[12px] tabular-nums text-amber-900 dark:text-amber-100"
    >
      <Warning size={16} weight="bold" className="text-amber-700 dark:text-amber-300 shrink-0" />
      <div className="flex-1">
        {t("ui.dashboard.first_run_rename_message")}
      </div>
      {isAdmin ? (
        // v0.6.8 fix — was a plain <a href>; see RegistrationsWarning.jsx
        // for why that wipes the in-progress workflow session.
        <Link
          to="/admin/clients"
          data-testid="first-run-rename-cta"
          className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 hover:text-amber-50 underline decoration-dotted font-semibold"
        >
          {t("ui.dashboard.first_run_rename_cta")}
          <ArrowSquareOut size={11} weight="bold" />
        </Link>
      ) : (
        <span
          data-testid="first-run-rename-operator-hint"
          className="text-amber-700/80"
        >
          {t("ui.dashboard.first_run_rename_operator_hint")}
        </span>
      )}
    </div>
  );
};
